import { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { Form, Input, Switch, Button, Space, Spin, Alert, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getPipeline,
  createPipeline,
  updatePipeline,
  type CreatePipelineRequest,
  type UpdatePipelineRequest,
} from '@/api/pipelineApi';
import { useAuth } from '@/hooks/useAuth';

const { TextArea } = Input;

interface PipelineFormValues {
  name: string;
  description?: string;
  is_default?: boolean;
}

export default function PipelineForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [form] = Form.useForm<PipelineFormValues>();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoading(true);
    setError(null);
    getPipeline(id)
      .then((pipeline) => {
        form.setFieldsValue({
          name: pipeline.name,
          description: pipeline.description || undefined,
          is_default: pipeline.is_default,
        });
      })
      .catch((err) => setError(err instanceof Error ? err.message : t('app.not_found')))
      .finally(() => setLoading(false));
  }, [isEdit, id, form, t]);

  const handleSubmit = async (values: PipelineFormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && id) {
        const data: UpdatePipelineRequest = {
          name: values.name,
          description: values.description || undefined,
          is_default: values.is_default,
        };
        await updatePipeline(id, data);
        message.success(t('pipelines.update_success'));
        navigate('/pipelines');
      } else {
        const data: CreatePipelineRequest = {
          name: values.name,
          description: values.description || undefined,
          is_default: values.is_default,
        };
        await createPipeline(data);
        message.success(t('pipelines.create_success'));
        navigate('/pipelines');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('app.error');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!hasPermission('pipelines', 'write')) {
    return <Navigate to="/pipelines" replace />;
  }

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/pipelines')}>
          {t('app.back')}
        </Button>
      </Space>

      <h2>{isEdit ? t('pipelines.edit_title') : t('pipelines.create_title')}</h2>

      {error && <Alert type="error" message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ is_default: false }}
        style={{ maxWidth: 800 }}
      >
        <Form.Item
          name="name"
          label={t('pipelines.field.name')}
          rules={[
            { required: true, message: t('pipelines.validation.name_required') },
            { max: 200, message: t('pipelines.validation.name_max_length') },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="description"
          label={t('pipelines.field.description')}
          rules={[{ max: 2000, message: t('pipelines.validation.description_max_length') }]}
        >
          <TextArea rows={4} />
        </Form.Item>

        <Form.Item name="is_default" label={t('pipelines.field.is_default')} valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {t('app.save')}
            </Button>
            <Button onClick={() => navigate('/pipelines')}>
              {t('app.cancel')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
